import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Section, Container, Eyebrow, DisplayH1, Reveal } from "@/components/Layout";
import { api } from "@/lib/api";
import { Heart, MapPin, ArrowUpRight } from "lucide-react";

function ProgressBar({ raised, goal }) {
  const pct = Math.min(100, Math.round((raised / goal) * 100));
  return (
    <div>
      <div className="h-1 bg-white/10 rounded-full overflow-hidden">
        <div className="h-full bg-[color:var(--wk-gold)]" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 flex justify-between text-xs">
        <span className="text-[color:var(--wk-bone)]">${raised.toLocaleString()}</span>
        <span className="text-[color:var(--wk-bone-dim)]">of ${goal.toLocaleString()} · {pct}%</span>
      </div>
    </div>
  );
}

export default function SponsorDogs() {
  const [dogs, setDogs] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/foundation/dogs")
      .then((r) => setDogs(r.data.dogs || []))
      .finally(() => setLoading(false));
  }, []);
  return (
    <>
      <Section testid="dogs-hero" className="pt-40 pb-16">
        <Container>
          <Eyebrow>Sponsor a Dog · Golden Wolf Foundation</Eyebrow>
          <DisplayH1 className="mt-6 max-w-4xl">Follow a life. Fund a story.</DisplayH1>
          <p className="mt-8 max-w-2xl text-lg text-[color:var(--wk-bone-dim)] leading-relaxed">
            Each dog in the Foundation has a name, a face, and a journey. Choose one to
            sponsor and receive monthly updates with photos, video clips, and milestones —
            straight from their trainer.
          </p>
        </Container>
      </Section>
      <Section testid="dogs-grid" className="pb-32">
        <Container>
          {loading && <div className="text-[color:var(--wk-bone-dim)]">Loading the pack…</div>}
          <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-3">
            {dogs.map((d) => (
              <Reveal key={d.dog_id}>
                <Link to={`/foundation/dogs/${d.dog_id}`} data-testid={`dog-card-${d.dog_id}`} className="card-lux overflow-hidden group block h-full">
                  <div className="relative aspect-[4/5] overflow-hidden">
                    <img src={d.image} alt={d.name} className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110" />
                    <div className="absolute inset-0 cinema-overlay" />
                    <div className="absolute top-4 left-4 overline text-[10px] px-3 py-1 rounded-full glass border-white/20">{d.stage}</div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="font-serif-display text-4xl leading-none">{d.name}</div>
                      <div className="mt-1 text-xs text-[color:var(--wk-bone-dim)] flex items-center gap-2">
                        <span>{d.breed} · {d.age_months} mo</span>
                        <span>·</span>
                        <MapPin size={11} />
                        <span>{d.location}</span>
                      </div>
                    </div>
                    <span className="absolute top-4 right-4 w-10 h-10 rounded-full hairline flex items-center justify-center text-[color:var(--wk-gold)] group-hover:bg-[color:var(--wk-gold)] group-hover:text-black transition-all">
                      <ArrowUpRight size={16} />
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-[color:var(--wk-bone-dim)] leading-relaxed line-clamp-3">{d.story}</p>
                    <div className="mt-5">
                      <ProgressBar raised={d.raised_usd} goal={d.goal_usd} />
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-xs text-[color:var(--wk-bone-dim)]">
                      <Heart size={12} className="text-[color:var(--wk-gold)]" />
                      {d.sponsors} sponsors following
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </Container>
      </Section>
    </>
  );
}
